// src/pages/po/CompraHistoricoModalContent.tsx
import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileInvoiceDollar, faRectangleXmark } from '@/icons';
import ScrollableTable from '../../components/common/ScrollableTable';
import { apiGet } from '../../services/api';
import { CompraHistoricoItem, CompraHistoricoResponse } from '../../types/compras/Historico';
import { formatDateOrDash } from '../../utils/formatUtils';
import './CompraHistoricoModalContent.css';
import { logger } from '@/utils/logger';

interface CompraHistoricoModalContentProps {
  compraHistorico: CompraHistoricoResponse;
  isOpen: boolean;
  onClose: () => void;
  theme: 'dark' | 'light';
  showCancelButton?: boolean;
}

const formatBRL = (v: number | null | undefined): string => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatQtdCompra = (v: number): string =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

const CompraHistoricoModalContent: React.FC<CompraHistoricoModalContentProps> = ({
  compraHistorico,
  isOpen,
  onClose,
  theme,
  showCancelButton = true,
}) => {
  const [items, setItems] = useState<CompraHistoricoItem[]>(compraHistorico.historicoCompras.content);
  const [hasMore, setHasMore] = useState(
    compraHistorico.historicoCompras.page.number < compraHistorico.historicoCompras.page.totalPages - 1
  );
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(compraHistorico.historicoCompras.page.number);

  const { codProduto, codCategoriaProduto, nomeProduto, nomeCategoria } = compraHistorico.produto;

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    try {
      const nextPage = currentPage + 1;
      const queryParams = new URLSearchParams({
        codProduto,
        codCategoriaProduto: codCategoriaProduto.toString(),
        page: nextPage.toString(),
        size: '10',
      });
      const res = await apiGet(`/v1/historico-compras-produto?${queryParams.toString()}`);
      const newData: CompraHistoricoItem[] = res.data.historicoCompras.content;
      const totalPages: number = res.data.historicoCompras.page.totalPages;
      setItems((prev) => [...prev, ...newData]);
      setHasMore(nextPage < totalPages - 1);
      setCurrentPage(nextPage);
    } catch (err) {
      logger.error('Erro ao carregar mais histórico de compras:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasMore, currentPage, codProduto, codCategoriaProduto]);

  if (!isOpen) return null;

  const isMissingValue = (v: string): boolean =>
    v === 'Erro de integração' || v === 'Não encontrado';

  const columns = [
    {
      header: 'Data',
      render: (entry: CompraHistoricoItem) => formatDateOrDash(entry.dataCompra),
    },
    {
      header: 'Loja',
      render: (entry: CompraHistoricoItem) =>
        isMissingValue(entry.nomeFantasiaLoja) ? (
          <span className="muted">{entry.nomeFantasiaLoja}</span>
        ) : (
          entry.nomeFantasiaLoja
        ),
    },
    {
      header: 'Comprador',
      render: (entry: CompraHistoricoItem) =>
        isMissingValue(entry.nomeComprador) ? (
          <span className="muted">{entry.nomeComprador}</span>
        ) : (
          entry.nomeComprador
        ),
    },
    {
      header: 'Nota Fiscal',
      render: (entry: CompraHistoricoItem) => entry.nroNotaFiscal,
    },
    {
      header: 'Fornecedor',
      render: (entry: CompraHistoricoItem) =>
        isMissingValue(entry.nomeFornecedor) ? (
          <span className="muted">{entry.nomeFornecedor}</span>
        ) : (
          entry.nomeFornecedor
        ),
    },
    {
      header: 'Qtd. Comprada',
      render: (entry: CompraHistoricoItem) => formatQtdCompra(entry.qtdCompra),
    },
    {
      header: 'Custo Unit.',
      render: (entry: CompraHistoricoItem) => formatBRL(entry.custoUnitario),
    },
    {
      header: 'Custo Total',
      render: (entry: CompraHistoricoItem) => formatBRL(entry.custoTotal),
    },
  ];

  return (
    <div className={`compra-historico-modal-content ${theme}`}>
      <div className="modal-header">
        <h4>
          <FontAwesomeIcon icon={faFileInvoiceDollar} style={{ marginRight: '8px' }} /> Histórico de Compra
        </h4>
      </div>
      <div className="item-info">
        <p>
          Código: {codProduto || ''} | Cód. Categoria: {codCategoriaProduto} | Nome do Produto:{' '}
          {nomeProduto || 'Erro de integração'} | Categoria: {nomeCategoria || 'Erro de integração'}
        </p>
      </div>
      <ScrollableTable<CompraHistoricoItem>
        data={items}
        hasMore={hasMore}
        loadMore={loadMore}
        isLoading={isLoading}
        columns={columns}
        onSelectChange={() => {}}
        showCheckboxes={false}
        isModal={true}
        emptyMessage="Nenhuma compra registrada para este produto."
      />
      {showCancelButton && (
        <div className="form-actions">
          <button onClick={onClose} className="cancel-button">
            <FontAwesomeIcon icon={faRectangleXmark} /> Cancelar
          </button>
        </div>
      )}
    </div>
  );
};

export default CompraHistoricoModalContent;
